import Global from '../global';
import CI from './client-integration';
import configParser from '../config/config-parser';
import SCAPI from '../services/sportclient-api/sportclient-api';
import { enums } from '@888sport/uc-sportclient-lib/dist/uc-sportclient-lib.min';

/**
 * @namespace DataProvider
 */
export default {
  async getSports(locale, market, offering, logData, useProdApis = false) {
    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getSports(locale, market, offering, logData, useProdApis);
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getSports(locale, market, offering, logData, useProdApis);
    }

    return promise;
  },

  async getPopular(locale, market, offering, logData, useProdApis = false) {
    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getQuickLinks(
        locale,
        market,
        offering,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getPopular(locale, market, offering, logData, useProdApis);
    }
    return promise;
  },

  async getCasinoGames(
    brand,
    subbrand,
    isMobile,
    logData,
    useProdApis = false
  ) {
    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getCasinoGames(
        brand,
        subbrand,
        isMobile,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getCasinoGames(
        brand,
        subbrand,
        isMobile,
        logData,
        useProdApis
      );
    }
    return promise;
  },

  async getSportCategories(
    langISO2,
    countryISO3,
    brand,
    popularTextTranslation,
    useProdApis = false
  ) {
    const localizationDetails = configParser.mapLangISO2ToLocale(langISO2);
    const { locale } = localizationDetails;
    const market = configParser.getMarketForCountry(countryISO3, brand);
    const offering = configParser.parseKambiOffering(brand, countryISO3);

    let logData = { offering, brand, langISO2, countryISO3 };

    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getSportCategories(
        langISO2,
        countryISO3,
        brand,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getSportCategories(
        locale,
        market,
        offering,
        popularTextTranslation,
        logData,
        useProdApis
      );
    }

    return promise;
  },

  getQuickLinks(locale, market, offering, logData, useProdApis = false) {
    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getQuickLinks(
        locale,
        market,
        offering,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getQuickLinks(
        locale,
        market,
        offering,
        logData,
        useProdApis
      );
    }

    return promise;
  },

  getSearchResults(
    langISO2,
    countryISO3,
    brand,
    searchTerm,
    useProdApis = false
  ) {
    const localizationDetails = configParser.mapLangISO2ToLocale(langISO2);
    const { locale } = localizationDetails;
    const market = configParser.getMarketForCountry(countryISO3, brand);
    const offering = configParser.parseKambiOffering(brand, countryISO3);

    let logData = { offering, brand, langISO2, countryISO3 };

    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getSearchResults(
        langISO2,
        countryISO3,
        brand,
        searchTerm,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getSearchResults(
        locale,
        market,
        offering,
        searchTerm,
        logData,
        useProdApis
      );
    }

    return promise;
  },

  getFavoritesSearchResults(
    searchTerm,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return CI.getFavoritesSearchResults(
      locale,
      market,
      offering,
      searchTerm,
      logData,
      useProdApis
    );
  },

  async getHighlightsStripData(
    sportGroups,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    // let promise = null;
    // if (process.env.BROWSER) {
    //   promise = SCAPI.getHighlightsStripData(sportGroups, locale, market, offering, logData, useProdApis);
    // } else {
    //   const SI = require("./server-integration").default;
    //   promise = SI.getHighlightsStripData(sportGroups, locale, market, offering, logData, useProdApis);
    // }
    //
    // return promise;

    let filteredGroups = sportGroups;

    Global.logger.info('[HIGH DEBUG] start getting HighlightsStripData...');

    if (!sportGroups || !sportGroups.length) {
      const { RACING_SPORTS } = enums;
      const filterRacingAndSpecialsCb = sportGroup =>
        !RACING_SPORTS.includes(sportGroup.sport) &&
        sportGroup.termKey !== '888_specials';

      Global.logger.info('[HIGH DEBUG] getQuickLinks from server side...');
      const highlightsGroups = await SCAPI.getQuickLinks(
        locale,
        market,
        offering,
        logData,
        useProdApis
      );
      Global.logger.info(
        `[HIGH DEBUG] received quickLinks from server side: ${JSON.stringify(
          highlightsGroups
        )}`
      );
      filteredGroups = highlightsGroups
        .filter(filterRacingAndSpecialsCb)
        .map(sportGroup => {
          return {
            id: sportGroup.id,
            sport: sportGroup.sport,
            sortOrder: sportGroup.sortOrder
          };
        });

      if (!filteredGroups.length) {
        Global.logger.info(
          '[HIGH DEBUG] no filteredGroups found - returning null... '
        );
        return null;
      }

      Global.logger.info(
        '[HIGH DEBUG] prepared quicklinks response for kambi call - calling kambi on client side...'
      );
    }

    return await CI.getEventsWithBetOffersHighlightsStrip(
      filteredGroups,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  getLiveEvents(locale, market, offering, logData, useProdApis = false) {
    return CI.getLiveEvents(locale, market, offering, logData, useProdApis);
  },

  /**
   * Get live events.
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<ILiveEventResponse | {}>}
   */
  getLiveEventsStripData(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return CI.getLiveEventsStripData(
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  getEventDetails(
    eventId,
    locale,
    market,
    offering,
    logData,
    useProdApis = false,
    shouldConvertData = true
  ) {
    return CI.getEventDetails(
      eventId,
      locale,
      market,
      offering,
      logData,
      useProdApis,
      shouldConvertData
    );
  },

  getListViewData(
    groupId,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const promise = CI.getListViewData(
      groupId,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
    if (promise) {
      return promise;
    } else {
      Global.logger.error(
        `NULL promise for getListViewData. process.env.BROWSER=${process.env.BROWSER}, lang=${locale}, market=${market}, offering=${offering}, useProdApis=${useProdApis} `
      );
      return null;
    }
  },

  getHighlights(locale, market, offering, logData, useProdApis = false) {
    return CI.getHighlights(locale, market, offering, logData, useProdApis);
  },

  getEnhancedSpecials(locale, market, offering, logData, useProdApis) {
    return CI.getEnhancedSpecials(
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  /**
   * Get next sport category
   * @param sportCategory
   * @param eventCount
   * @param locale
   * @param market
   * @param offering
   * @param logData
   * @param useProdApis
   * @return {*}
   */
  getNextSportCategory(
    sportCategory,
    eventCount,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    return CI.getNextSportCategory(
      sportCategory,
      eventCount,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  /**
   * Get meetings for sport category
   * @memberOf DataProvider
   * @param {string} sportCategory - Code for sports. Example: horse_racing
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {any} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KMeeting[]>} - Array of meetings that contain racing events.
   */
  getMeetingsForSportCategory(
    sportCategory,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    return CI.getMeetingsForSportCategory(
      sportCategory,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  /**
   * Gets events and bet offers by event ID array.
   * @param {number[]} eventIds - Array of event IDs to get.
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {any} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<{outcomesMap: IOutcomesMap, betOffersMap: IBetOffersMap, eventsMap: IEventsMap, participantsMap: IParticipantsMap}>}
   */
  getEventsAndBetOffersByEventIds(
    eventIds,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return CI.getEventsAndBetOffersByEventIds(
      eventIds,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  getBetOffersForEvents(
    eventIds,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return CI.getBetOffersForEvents(
      eventIds,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  },

  initSportProviderSocket(store) {
    CI.initSportProviderSocket(store);
  },

  loadDataForCashoutableBetsStrip(userContext, useProdApis) {
    return CI.loadDataForCashoutableBetsStrip(userContext, useProdApis);
  },

  getBetOfferOutcome(selectionId, userContext, useProdApis) {
    return CI.getBetOfferOutcome(selectionId, userContext, useProdApis);
  },

  makeCashIn(payload, useProdApis) {
    return CI.makeCashIn(payload, useProdApis);
  },

  /*getOpenBets(userContext, useProdApis) {
    return CI.getOpenBets(userContext, useProdApis);
  },*/

  performLogin(userContext, initialSportClientToken, useProdApis) {
    return CI.performLogin(userContext, initialSportClientToken, useProdApis);
  },

  extendSession(userContext, useProdApis) {
    return CI.extendSession(userContext, useProdApis);
  },

  getIconsSVG() {
    return SCAPI.getIconsSVG();
  },

  validateAndGetFavoritesInitialData(logData, token, useProdApis = false) {
    return CI.validateAndGetFavoritesInitialData(logData, token, useProdApis);
  },

  setComingSoon(logData, notified, token, useProdApis = false) {
    return CI.setComingSoon(logData, notified, token, useProdApis);
  },

  setEndgame(logData, notified, token, useProdApis = false) {
    return CI.setEndgame(logData, notified, token, useProdApis);
  },

  followFavorite(
    logOn,
    favoriteToken,
    sportType,
    itemName,
    locale,
    market,
    offering,
    useProdApis = false
  ) {
    return CI.followFavorite(
      logOn,
      favoriteToken,
      sportType,
      itemName,
      locale,
      market,
      offering,
      useProdApis
    );
  },

  unfollowFavorite(
    logOn,
    favoriteToken,
    sportType,
    itemName,
    useProdApis = false
  ) {
    return CI.unfollowFavorite(
      logOn,
      favoriteToken,
      sportType,
      itemName,
      useProdApis
    );
  },

  getBetsRecommendations(
    cid,
    systemId,
    topRecommendations,
    logData,
    useProdApis
  ) {
    let promise = null;
    if (process.env.BROWSER) {
      promise = SCAPI.getBetsRecommendations(
        cid,
        systemId,
        topRecommendations,
        logData,
        useProdApis
      );
    } else {
      const SI = require('./server-integration').default;
      promise = SI.getBetsRecommendations(
        cid,
        systemId,
        topRecommendations,
        logData,
        useProdApis
      );
    }

    return promise;
  }
};
